import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Alert,
  Chip,
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  Error,
  Info,
  AccountBalanceWallet,
  SwapHoriz,
  Verified,
} from "@mui/icons-material";
import ValidationStatusBar from "./ValidationStatusBar";
import BalanceChecker from "./BalanceChecker";
import TradeStatusIndicator from "./TradeStatusIndicator";
import EnhancedPositionForm from "./EnhancedPositionForm";
import TradeReopeningWorkflow from "./TradeReopeningWorkflow";
import PositionAdjustmentSuggestions from "./PositionAdjustmentSuggestions";
import { PositionSuggestion } from "../types/position";
import "./ValidationDemo.css";

const ValidationDemo: React.FC = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [showWorkflow, setShowWorkflow] = useState(false);

  const mockValidationResult = {
    isValid: false,
    errors: [
      "Insufficient wallet balance for this position",
      "Trade is currently closed and cannot be modified",
    ],
    warnings: [
      "Position size exceeds recommended risk limits",
      "Consider reducing quantity to meet margin requirements",
    ],
    suggestedActions: [
      "Deposit additional funds to wallet",
      "Reopen the trade to enable modifications",
      "Reduce position quantity to meet balance requirements",
    ],
  };

  const mockBalanceCheck = {
    hasSufficientBalance: false,
    requiredAmount: 5000,
    currentBalance: 3200,
    shortfall: 1800,
    suggestedDeposit: 2000,
  };

  const mockPositionSuggestions: PositionSuggestion[] = [
    {
      id: "1",
      type: "quantity",
      currentValue: 100,
      suggestedValue: 75,
      reason: "Reduces risk and meets margin requirements",
      impact: "medium",
    },
    {
      id: "2",
      type: "price",
      currentValue: 50.25,
      suggestedValue: 50.0,
      reason: "Better entry point based on current market conditions",
      impact: "low",
    },
    {
      id: "3",
      type: "stopLoss",
      currentValue: 45.0,
      suggestedValue: 47.5,
      reason: "Tighter stop loss to minimize potential losses",
      impact: "high",
    },
  ];

  const sections = [
    { id: "overview", label: "Overview", icon: <Info /> },
    { id: "validation", label: "Validation Status", icon: <Verified /> },
    {
      id: "balance",
      label: "Balance Checking",
      icon: <AccountBalanceWallet />,
    },
    { id: "trade-status", label: "Trade Status", icon: <SwapHoriz /> },
    {
      id: "position-form",
      label: "Enhanced Position Form",
      icon: <CheckCircle />,
    },
    { id: "suggestions", label: "Position Suggestions", icon: <Warning /> },
    { id: "workflow", label: "Trade Reopening", icon: <SwapHoriz /> },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Validation System Overview
            </Typography>
            <Typography variant="body1" paragraph>
              This demo showcases the comprehensive validation system
              implemented for positions and trades. The system provides
              real-time feedback, prevents invalid operations, and guides users
              through problem resolution.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
                mt: 2,
              }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <CheckCircle
                      color="success"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Real-time Validation
                  </Typography>
                  <Typography variant="body2">
                    Instant feedback on form inputs, balance checks, and trade
                    status validation.
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Warning
                      color="warning"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Smart Suggestions
                  </Typography>
                  <Typography variant="body2">
                    Intelligent recommendations for position adjustments and
                    problem resolution.
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <AccountBalanceWallet
                      color="primary"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Balance Management
                  </Typography>
                  <Typography variant="body2">
                    Automatic balance checking with deposit suggestions and fee
                    calculations.
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <SwapHoriz
                      color="secondary"
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Trade Workflows
                  </Typography>
                  <Typography variant="body2">
                    Guided processes for reopening trades and managing position
                    statuses.
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Note:</strong> This demo uses mock data to showcase the
                UI components. In production, these components will connect to
                real backend validation services.
              </Typography>
            </Alert>
          </Box>
        );

      case "validation":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Validation Status Display
            </Typography>
            <Typography variant="body1" paragraph>
              The ValidationStatusBar component provides a clear, organized view
              of validation results, including errors, warnings, and suggested
              actions.
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Example: Position Creation Validation
              </Typography>
              <ValidationStatusBar validation={mockValidationResult} />
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Validation States
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip
                  icon={<CheckCircle />}
                  label="Valid"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<Warning />}
                  label="Warnings"
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  icon={<Error />}
                  label="Errors"
                  color="error"
                  variant="outlined"
                />
                <Chip
                  icon={<Info />}
                  label="Suggestions"
                  color="info"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>
        );

      case "balance":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Balance Checking & Management
            </Typography>
            <Typography variant="body1" paragraph>
              The BalanceChecker component automatically verifies wallet balance
              against required amounts and provides intelligent deposit
              suggestions.
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Example: Insufficient Balance Scenario
              </Typography>
              <BalanceChecker
                tradeId={123}
                quantity={100}
                price={50}
                onDeposit={() =>
                  alert("Deposit functionality would be implemented here")
                }
              />
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Balance Check Features
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      <CheckCircle
                        color="success"
                        sx={{ mr: 1, verticalAlign: "middle" }}
                      />
                      Automatic Calculation
                    </Typography>
                    <Typography variant="body2">
                      Calculates required amount including fees, taxes, and
                      margin requirements.
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      <Info
                        color="info"
                        sx={{ mr: 1, verticalAlign: "middle" }}
                      />
                      Smart Suggestions
                    </Typography>
                    <Typography variant="body2">
                      Recommends deposit amounts with buffer for future
                      positions.
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        );

      case "trade-status":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Trade Status Management
            </Typography>
            <Typography variant="body1" paragraph>
              The TradeStatusIndicator shows current trade status and provides
              options for reopening closed trades when needed.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 3,
              }}
            >
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Open Trade
                </Typography>
                <TradeStatusIndicator
                  tradeId="123"
                  isClosed={false}
                  onReopen={() => {}}
                />
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Closed Trade
                </Typography>
                <TradeStatusIndicator
                  tradeId="456"
                  isClosed={true}
                  onReopen={() => setShowWorkflow(true)}
                />
              </Paper>
            </Box>

            {showWorkflow && (
              <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Trade Reopening Workflow
                </Typography>
                <TradeReopeningWorkflow
                  tradeId="456"
                  onClose={() => setShowWorkflow(false)}
                />
              </Paper>
            )}
          </Box>
        );

      case "position-form":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Enhanced Position Form
            </Typography>
            <Typography variant="body1" paragraph>
              The EnhancedPositionForm integrates all validation components and
              provides a comprehensive form experience with real-time feedback.
            </Typography>

            <Paper sx={{ p: 3 }}>
              <EnhancedPositionForm
                tradeId="123"
                initialData={{
                  symbol: "AAPL",
                  quantity: 100,
                  price: 150.0,
                  type: "LONG",
                  openDate: new Date(),
                }}
                onSave={(data) =>
                  alert(`Position saved: ${JSON.stringify(data)}`)
                }
                onCancel={() => {}}
              />
            </Paper>
          </Box>
        );

      case "suggestions":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Position Adjustment Suggestions
            </Typography>
            <Typography variant="body1" paragraph>
              The PositionAdjustmentSuggestions component provides intelligent
              recommendations for optimizing position parameters based on
              current market conditions and risk management.
            </Typography>

            <Paper sx={{ p: 3 }}>
              <PositionAdjustmentSuggestions
                suggestions={mockPositionSuggestions}
                onApplySuggestion={(suggestion) =>
                  alert(
                    `Applied ${suggestion.type}: ${suggestion.suggestedValue}`,
                  )
                }
              />
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Suggestion Impact Levels
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Chip label="Low Impact" color="success" variant="outlined" />
                <Chip
                  label="Medium Impact"
                  color="warning"
                  variant="outlined"
                />
                <Chip label="High Impact" color="error" variant="outlined" />
              </Box>
            </Box>
          </Box>
        );

      case "workflow":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Trade Reopening Workflow
            </Typography>
            <Typography variant="body1" paragraph>
              The TradeReopeningWorkflow guides users through the process of
              reopening closed trades and managing associated position statuses.
            </Typography>

            <Paper sx={{ p: 3 }}>
              <TradeReopeningWorkflow tradeId="456" onClose={() => {}} />
            </Paper>

            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Workflow Steps
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                  gap: 2,
                }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      1. Review Positions
                    </Typography>
                    <Typography variant="body2">
                      Examine all positions associated with the trade.
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      2. Update Statuses
                    </Typography>
                    <Typography variant="body2">
                      Modify position statuses as needed.
                    </Typography>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      3. Confirm Changes
                    </Typography>
                    <Typography variant="body2">
                      Review and confirm all modifications.
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box className="validation-demo">
      <Box className="demo-header">
        <Typography variant="h4" component="h1" gutterBottom>
          <Verified sx={{ mr: 2, verticalAlign: "middle" }} />
          Validation System Demo
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Explore the comprehensive validation features for positions and trades
        </Typography>
      </Box>

      <Box className="demo-navigation">
        {sections.map((section) => (
          <Button
            key={section.id}
            variant={activeSection === section.id ? "contained" : "outlined"}
            startIcon={section.icon}
            onClick={() => setActiveSection(section.id)}
            sx={{ mr: 1, mb: 1 }}
          >
            {section.label}
          </Button>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box className="demo-content">{renderSection()}</Box>
    </Box>
  );
};

export default ValidationDemo;
